import frappe, { WAREHOUSE_PRINCIPAL, mapPaymentMode } from './frappe';
import { getOrderProducts } from './getOrders';

const today = () => new Date().toISOString().split('T')[0];

export async function getOrderTotal(orderId) {
  try {
    const { data } = await frappe.get(
      `/api/resource/Sales%20Order/${orderId}?fields=["grand_total"]`
    );
    return data.data.grand_total;
  } catch (error) {
    console.error('Error al obtener el total de la orden:', error.response?.data || error.message);
    throw error;
  }
}

// Descuento del 10% usando el campo nativo de Frappe en lugar de líneas negativas
export async function applyDiscountToOrder(orderId, total) {
  try {
    const { data: { data: so } } = await frappe.get(`/api/resource/Sales%20Order/${orderId}`);
    so.additional_discount_percentage = 10;
    const { data: result } = await frappe.put(`/api/resource/Sales%20Order/${orderId}`, so);
    return result.data;
  } catch (error) {
    console.error('Error al aplicar descuento:', error.response?.data || error.message);
    throw error;
  }
}

export async function createShoppingCart(customerName) {
  try {
    const { data } = await frappe.post('/api/resource/Sales%20Order', {
      customer: customerName,
      transaction_date: today(),
      delivery_date: today(),
      items: [],
      docstatus: 0,
    });
    return data.data.name;
  } catch (error) {
    console.error('Error al crear carrito:', error.response?.data || error.message);
    return null;
  }
}

// En Frappe actualizamos el mismo draft SO con warehouse y método de pago,
// en lugar de crear uno nuevo. Retorna el mismo ID (comportamiento equivalente).
export async function migrateDraftToNewOrder(draftOrderId, userID, warehouseId, paymentMethod) {
  try {
    const products = await getOrderProducts(draftOrderId);
    const t = today();

    // Crear la orden real con los datos del checkout
    const { data: newSO } = await frappe.post('/api/resource/Sales%20Order', {
      customer: userID,
      transaction_date: t,
      delivery_date: t,
      set_warehouse: warehouseId,
      custom_mode_of_payment: mapPaymentMode(paymentMethod),
      items: products.map(p => ({
        item_code: p.id,
        qty: p.qty,
        rate: p.qty > 0 ? p.total_ttc / p.qty : 0,
        warehouse: warehouseId,
        delivery_date: t,
      })),
      docstatus: 0,
    });

    const newOrderId = newSO.data.name;

    // Limpiar el draft original para que sirva como carrito vacío
    const { data: { data: draftSO } } = await frappe.get(`/api/resource/Sales%20Order/${draftOrderId}`);
    draftSO.items = [];
    await frappe.put(`/api/resource/Sales%20Order/${draftOrderId}`, draftSO);

    return newOrderId;
  } catch (error) {
    console.error('Error en migración de orden:', error.response?.data || error.message);
    throw error;
  }
}

export async function createPCBOrder(userID, product, warehouseId, paymentMethod) {
  try {
    const t = today();
    const { data } = await frappe.post('/api/resource/Sales%20Order', {
      customer: userID,
      transaction_date: t,
      delivery_date: t,
      set_warehouse: warehouseId,
      custom_mode_of_payment: mapPaymentMode(paymentMethod),
      items: [{
        item_code: product.fk_product,
        qty: product.qty,
        rate: product.subprice,
        warehouse: warehouseId,
        delivery_date: t,
      }],
      docstatus: 0,
    });
    return data.data.name;
  } catch (error) {
    console.error('Error al crear orden PCB:', error.response?.data || error.message);
    throw error;
  }
}

// Añadir producto al carrito (GET → push → PUT)
export async function addProductToOrder(idOrder, productData) {
  try {
    const { data: { data: so } } = await frappe.get(`/api/resource/Sales%20Order/${idOrder}`);
    so.items.push({
      item_code: productData.id,
      qty: productData.qty,
      rate: productData.subprice,
      warehouse: so.set_warehouse || WAREHOUSE_PRINCIPAL,
      delivery_date: so.delivery_date || today(),
    });
    const { data: result } = await frappe.put(`/api/resource/Sales%20Order/${idOrder}`, so);
    const newItem = result.data.items[result.data.items.length - 1];
    return newItem?.name;
  } catch (error) {
    console.error('Error al añadir producto:', error.response?.data || error.message);
    throw error;
  }
}

// qty y subprice → rate en Frappe (GET → update → PUT)
export async function updateProductInOrder(orderId, lineId, updatedData) {
  try {
    const { data: { data: so } } = await frappe.get(`/api/resource/Sales%20Order/${orderId}`);
    const item = so.items.find(i => i.name === lineId);
    if (!item) return null;
    if (updatedData.qty !== undefined) item.qty = updatedData.qty;
    if (updatedData.subprice !== undefined) item.rate = updatedData.subprice;
    const { data: result } = await frappe.put(`/api/resource/Sales%20Order/${orderId}`, so);
    return result.data;
  } catch (error) {
    console.error('Error al actualizar producto:', error.response?.data || error.message);
    throw error;
  }
}

export async function deleteProductFromOrder(orderId, lineId) {
  try {
    const { data: { data: so } } = await frappe.get(`/api/resource/Sales%20Order/${orderId}`);
    so.items = so.items.filter(i => i.name !== lineId);
    const { data: result } = await frappe.put(`/api/resource/Sales%20Order/${orderId}`, so);
    return result.data;
  } catch (error) {
    console.error('Error al eliminar producto:', error.response?.data || error.message);
    throw error;
  }
}

export async function updateOrderData(orderId, warehouseId, paymentMethodId) {
  try {
    const { data: { data: so } } = await frappe.get(`/api/resource/Sales%20Order/${orderId}`);
    so.set_warehouse = warehouseId;
    const { data: result } = await frappe.put(`/api/resource/Sales%20Order/${orderId}`, so);
    return result.data;
  } catch (error) {
    console.error('Error al actualizar orden:', error.response?.data || error.message);
    throw error;
  }
}

export async function validateOrder(orderId) {
  try {
    // frappe.client.submit requires the full current document (including its
    // `modified` timestamp) to submit, otherwise Frappe rejects it with
    // TimestampMismatchError, treating it as a stale/conflicting edit.
    const { data: { data: doc } } = await frappe.get(`/api/resource/Sales%20Order/${orderId}`);
    const { data } = await frappe.post('/api/method/frappe.client.submit', { doc });
    return data.message;
  } catch (error) {
    console.error('Error al validar orden:', error.response?.data || error.message);
    throw error;
  }
}

// En Frappe la orden se marca como facturada automáticamente al crear el Sales Invoice
export const setOrderAsInvoiced = async (orderId) => {
  return true;
};

export async function addCRShippingCost(orderId, producto) {
  // Requiere un item de tipo "Shipping" en Frappe con item_code = "SHIPPING-CR"
  try {
    const { data: { data: so } } = await frappe.get(`/api/resource/Sales%20Order/${orderId}`);
    so.items.push({
      item_code: 'SHIPPING-CR',
      item_name: `La orden incluye un costo por envío: ${producto.label}`,
      qty: producto.qty,
      rate: producto.subprice,
      warehouse: so.set_warehouse || WAREHOUSE_PRINCIPAL,
      delivery_date: so.delivery_date || today(),
    });
    await frappe.put(`/api/resource/Sales%20Order/${orderId}`, so);
  } catch (error) {
    console.error('Error al añadir costo de envío:', error.response?.data || error.message);
    throw error;
  }
}

// Descuentos de kit: líneas con precio negativo (requiere item DISCOUNT-KIT en Frappe)
export async function discountKit(orderId, products) {
  try {
    const { data: { data: so } } = await frappe.get(`/api/resource/Sales%20Order/${orderId}`);
    for (const producto of products) {
      so.items.push({
        item_code: 'DISCOUNT-KIT',
        item_name: `El kit no incluirá: ${producto.label}`,
        qty: producto.qty,
        rate: -Math.abs(producto.subprice),
        warehouse: so.set_warehouse || WAREHOUSE_PRINCIPAL,
        delivery_date: so.delivery_date || today(),
      });
    }
    await frappe.put(`/api/resource/Sales%20Order/${orderId}`, so);
  } catch (error) {
    console.error('Error al aplicar descuento de kit:', error.response?.data || error.message);
    throw error;
  }
}

export async function additionalForKit(orderId, products) {
  try {
    const { data: { data: so } } = await frappe.get(`/api/resource/Sales%20Order/${orderId}`);
    for (const producto of products) {
      so.items.push({
        item_code: producto.id,
        qty: producto.qty,
        rate: producto.subprice,
        warehouse: so.set_warehouse || WAREHOUSE_PRINCIPAL,
        delivery_date: so.delivery_date || today(),
      });
    }
    await frappe.put(`/api/resource/Sales%20Order/${orderId}`, so);
  } catch (error) {
    console.error('Error al añadir adicional de kit:', error.response?.data || error.message);
    throw error;
  }
}

export async function posKitOrder(userData, kit, discountProducts, additionalProducts) {
  try {
    const t = today();
    const items = [
      {
        item_code: kit.fk_product,
        qty: kit.qty,
        rate: kit.subprice,
        warehouse: userData.warehouse_id || WAREHOUSE_PRINCIPAL,
        delivery_date: t,
      },
    ];

    const { data } = await frappe.post('/api/resource/Sales%20Order', {
      customer: userData.socid,
      transaction_date: t,
      delivery_date: t,
      set_warehouse: userData.warehouse_id || WAREHOUSE_PRINCIPAL,
      custom_mode_of_payment: mapPaymentMode(userData.mode_reglement_id),
      items,
      docstatus: 0,
    });

    const orderId = data.data.name;

    if (discountProducts?.length > 0) await discountKit(orderId, discountProducts);
    if (additionalProducts?.length > 0) await additionalForKit(orderId, additionalProducts);

    return orderId;
  } catch (error) {
    console.error('Error al crear orden de kit:', error.response?.data || error.message);
    throw error;
  }
}

// En Frappe "cerrar" una orden ocurre automáticamente cuando se completan
// las entregas y facturas. Se puede forzar cambiando el status a Closed.
export const closeOrder = async (orderId) => {
  try {
    const { data } = await frappe.put(`/api/resource/Sales%20Order/${orderId}`, {
      status: 'Closed',
    });
    return data.data;
  } catch (error) {
    console.error('Error al cerrar orden:', error.response?.data || error.message);
    return null;
  }
};
